<template>
    <div>
        <DModal
            :id="mailModalId"
            :show="showModal"
            :title="__(`tools.emails.samples.${selectedEmailType.type}.${selectedSample}`)"
            is-big
            close-color-secondary
            overflow-auto
            @close="closeModal"
        >
            <div class="modal-header">
                <DSelect
                    id="brand"
                    v-model="brand"
                    :options="brandOptions"
                    style="flex-grow: 1"
                    class="m-0"
                    @update:value="handleBrandChange"
                />
                <DSelect
                    id="email_type"
                    v-model="selectedEmailType.type"
                    :disabled="!!emailType"
                    :options="emailTypeOptions"
                    style="flex-grow: 1"
                    class="m-0"
                    @update:value="handleEmailTypeChange"
                />
                <DSelect
                    id="email_sample"
                    v-model="selectedSample"
                    :options="emailSampleOptions"
                    style="flex-grow: 1"
                    class="m-0"
                    @update:value="handleSampleTypeChange"
                />
                <DButton
                    :loading="loading"
                    icon="eye"
                    :label="__('tools.emails.modal.display')"
                    :color="'primary'"
                    @click="previewEmail()"
                />
                <DButton
                    icon="download"
                    :disabled="isInLoadingState || !previewSuccessful || !sampleData.eml"
                    :label="__('tools.emails.modal.download')"
                    @click="downloadSampleDocument()"
                />
            </div>

            <div
                v-if="!isInLoadingState && (!previewSuccessful || !sampleData.eml)"
                class="select-prompt"
            >
                <div>
                    <DIcon
                        name="form-dropdown"
                        :size="32"
                    />
                    <p v-html="__('tools.emails.modal.select_a_tenant')" />
                </div>
            </div>

            <div
                v-if="previewSuccessful"
                class="modal-base"
            >
                <DTabs>
                    <DTabItem
                        :label="__('tools.emails.modal.tabs.html')"
                        :active="currentTab === 0"
                        @click="changeTab(0)"
                    />
                    <DTabItem
                        :label="__('tools.emails.modal.tabs.plain_text')"
                        :active="currentTab === 1"
                        @click="changeTab(1)"
                    />
                    <DTabItem
                        :label="__('tools.emails.modal.tabs.send_copy')"
                        :active="currentTab === 2"
                        @click="changeTab(2)"
                    />
                </DTabs>

                <EmailPreviewEnvelopeDetails
                    :sample-data="sampleData"
                />

                <DCard
                    v-if="currentTab === 0"
                    class="ignore-parent-styles"
                >
                    <div
                        v-if="previewSuccessful"
                        v-html="divFromHTMLBody()"
                    />
                </DCard>

                <DCard
                    v-if="currentTab === 1"
                    padding
                >
                    <div
                        class="raw-textbox"
                        v-html="sampleData.text"
                    />
                </DCard>

                <DCard
                    v-if="currentTab === 2"
                    padding
                >
                    <div class="mt-3">
                        <DInput
                            id="email"
                            v-model="email"
                            :label="__('tools.emails.modal.email')"
                            add-label-classes="required"
                        />
                        <p
                            v-for="(error, index) in errors"
                            :key="index"
                            class="help-block text-danger"
                        >
                            {{ error }}
                        </p>
                        <div class="d-flex justify-content-start align-items-center">
                            <DButton
                                :loading="sendingEmailLoading"
                                :label="__('tools.emails.modal.tabs.send_copy')"
                                :disabled="!email"
                                :color="'primary'"
                                @click="sendEmail()"
                            />
                            <p
                                v-show="mailSentSuccessfully"
                                class="text-success ms-3 mb-0 p-0"
                            >
                                {{ __('tools.emails.modal.mail_sent') }}
                            </p>
                        </div>
                    </div>
                </DCard>
            </div>

            <div
                v-if="errors.length > 0 && !email"
                class="modal-error"
            >
                <DAlert
                    v-for="(error, index) in errors"
                    :key="index"
                    color="danger"
                >
                    {{ error }}
                </DAlert>
            </div>
            <template #actions>
                <DButton
                    :label="__('messages.save')"
                    color="primary"
                    :disabled="!previewSuccessful"
                    @click="handleSaveClick"
                />
            </template>
        </DModal>
        <DModal
            v-model:show="showSaveConfirmationModal"
            :title="__('tools.emails.modal.confirmation_modal_title')"
            is-confirm
            :message="__('tools.emails.modal.confirmation_modal_message')"
            @confirm="handleSaveContent"
            @close="showSaveConfirmationModal=false"
        />
    </div>
</template>

<script>
/* eslint vue/no-v-html: 0 */

import NProgress from 'nprogress'
import _ from 'lodash'
import preventEmailUrl from '~@/Mixins/preventEmailUrl'
import EmailPreviewEnvelopeDetails from '~@/Components/Tools/Emails/EmailPreviewEnvelopeDetails.vue'

export default {
    name: 'EmailEditPreviewModal',
    components: {
        EmailPreviewEnvelopeDetails
    },
    mixins: [
        preventEmailUrl
    ],
    props: {
        showModal: {
            type: Boolean,
            default: false
        },
        emailType: {
            type: String,
            default: ''
        },
        sample: {
            type: String,
            default: ''
        },
        brands: {
            type: Array,
            default: () => { return [] }
        },
        mailables: {
            type: Array,
            default: () => { return [] }
        },
        form: {
            type: Object,
            default: () => { return {} }
        },
        samples: {
            type: Array,
            default: () => { return [] }
        },
        previewRoute: {
            type: String,
            default: ''
        },
        contentUpdateRoute: {
            type: String,
            default: ''
        }
    },
    emits: ['modalError', 'closeModal'],
    data () {
        return {
            brand: this.brands[0]?.id,
            loading: false,
            sendingEmailLoading: false,
            mailSentSuccessfully: false,
            errors: [],
            sampleData: null,
            fileName: null,
            email: '',
            selectedEmailType: this.mailables[0],
            selectedSample: this.mailables[0].samples[0],
            currentTab: 0,
            eml: null,
            showSaveConfirmationModal: false,
            mailModalId: 'mailModalId'
        }
    },
    computed: {
        brandOptions () {
            return this.brands.map(brand => {
                return {
                    value: brand.id,
                    label: brand.name
                }
            })
        },
        previewSuccessful () {
            return this.sampleData !== null
        },
        emailTypeOptions () {
            return this.mailables.map(mailable => {
                return {
                    value: mailable.type,
                    label: this.__('tools.emails.email.' + mailable.type + '.name')
                }
            })
        },
        emailSampleOptions () {
            return this.selectedEmailType.samples.map(sample => {
                return {
                    value: sample,
                    label: this.__('tools.emails.samples.' + this.selectedEmailType.type + '.' + sample)
                }
            })
        }
    },
    updated () {
        this.preventEmailUrl(this.mailModalId)
    },
    methods: {
        changeTab (tabIndex) {
            this.currentTab = tabIndex
        },
        handleEmailTypeChange (value) {
            this.mailables.forEach(mailable => {
                if (mailable.type === value) {
                    this.selectedEmailType = {
                        type: mailable.type,
                        samples: mailable.samples
                    }
                    this.selectedSample = this.selectedEmailType.samples[0]
                }
            })
            this.sampleData = null
        },
        handleSampleTypeChange () {
            this.sampleData = null
        },
        handleBrandChange () {
            this.sampleData = null
        },
        previewEmail () {
            NProgress.start()

            this.loading = true
            this.sampleData = null
            this.errors = []

            const template = _.cloneDeep(this.form.data())

            delete template.brand

            window.axios.post(this.previewRoute, {
                emailType: this.selectedEmailType.type,
                sample: this.selectedSample,
                brand: this.brand,
                template: template,
                language: template.language
            })
                .then(response => {
                    NProgress.done()
                    this.loading = false
                    this.sampleData = response.data
                    this.eml = response.data.eml
                })
                .catch((error) => {
                    NProgress.done()

                    this.loading = false
                    const errorObject = error.response && error.response.data && error.response.data.errors
                        ? error.response.data.errors
                        : {}
                    Object.keys(errorObject).forEach((field) => {
                        errorObject[field].forEach((message) => {
                            this.errors.push(message)
                        })
                    })
                    this.$emit('modalError', errorObject)
                })
        },
        downloadSampleDocument () {
            const link = document.createElement('a')
            link.download = 'example.eml'
            const blob = new Blob([this.sampleData.eml], {
                type: 'text/plain'
            })
            link.href = window.URL.createObjectURL(blob)
            link.click()
        },
        sendEmail () {
            NProgress.start()

            const template = _.cloneDeep(this.form.data())

            delete template.brand

            this.sendingEmailLoading = true
            this.mailSentSuccessfully = false
            this.errors = []

            window.axios.post(this.$route('tools.emails.send-preview-email'), {
                emailType: this.selectedEmailType.type,
                sample: this.selectedSample,
                brand: this.brand,
                template: template,
                language: template.language,
                email: this.email
            })
                .then(() => {
                    NProgress.done()

                    this.sendingEmailLoading = false
                    this.mailSentSuccessfully = true
                })
                .catch((error) => {
                    NProgress.done()

                    const errorObject = error.response && error.response.data && error.response.data.errors
                        ? error.response.data.errors
                        : {}
                    Object.keys(errorObject).forEach((field) => {
                        errorObject[field].forEach((message) => {
                            this.errors.push(message)
                        })
                    })
                    this.sendingEmailLoading = false
                })
        },
        closeModal () {
            this.sampleData = null
            this.fileName = null
            this.errors = []
            this.email = ''
            this.mailSentSuccessfully = false

            this.$emit('closeModal')
        },
        divFromHTMLBody () {
            return this.sampleData.html.split('</head>')[1].split('</html>')[0]
                .replace('<body ', '<div ')
                .replace('</body>', '</div>')
        },
        handleSaveContent () {
            this.form.post(this.contentUpdateRoute, {
                onFinish: () => this.closeModal(),
                onError: (error) => {
                    const errors = []

                    for (const fieldError in error) {
                        errors[fieldError] = [error[fieldError]]
                    }

                    this.$emit('modalError', errors)
                }
            })
        },
        handleSaveClick () {
            this.$emit('closeModal')
            this.showSaveConfirmationModal = true
        }
    }
}
</script>

<style lang="scss" scoped>
.modal-header {
    display: flex;
    grid-gap: 15px;
    padding: 15px;
    align-items: center;
}
.modal-base {
    margin: 15px;
    margin-top: 0px;
    display: grid;
    grid-gap: 15px;

    & .raw-textbox {
        font-family: monospace, monospace;
        white-space: pre-wrap;
    }
}
.modal-error {
    margin: 15px;
    margin-top: 0px;
    display: grid;
    grid-gap: 15px;
}

.select-prompt {
    height: 250px;
    display: flex;
    align-items: center;
    justify-content: center;

    & div {
        display: flex;
        flex-direction: column;
        grid-gap: 15px;
        align-items: center;

        & p {
            text-align: center;
            line-height: 1.5em;
            color: rgba(0,0,0,0.6);
        }
    }
}
</style>
